import type { BaseApiResponse } from './types/api.types';

import { destroy, put, get } from './axios';

export interface TrainerPackageOption {
  userMembershipId: string;
  name: string;
  trainerId: null | string;
  /** null = the package has no visit limit */
  sessionsLeft: null | number;
}

export interface TrainerPackages {
  packages: TrainerPackageOption[];
  /** The one a mark would take from when nobody chose, if it is decidable */
  defaultUserMembershipId: null | string;
}

export interface FetchTrainerPackagesArgs {
  organizationId: string;
  clientId: string;
  /** The trainer who assigned the workout, to break a tie between packages */
  assignedByTrainerId?: string;
}

export interface TrainerSessionArgs {
  organizationId: string;
  clientId: string;
  feedbackId: string;
}

export interface MarkTrainerSessionArgs extends TrainerSessionArgs {
  userMembershipId?: string;
  assignedByTrainerId?: string;
}

export type TrainerSessionNotSpent =
  | 'AMBIGUOUS'
  | 'NO_ORGANIZATION'
  | 'NO_PACKAGE'
  | 'OUTSIDE_PERIOD'
  | 'USED_UP';

export interface TrainerSessionResult {
  id: string;
  completedWith: string | null;
  trainerSessionUserMembershipId: null | string;
  spentFromUserMembershipId?: null | string;
  notSpent?: null | TrainerSessionNotSpent;
}

const base = (organizationId: string, clientId: string) =>
  `/v2/organizations/${organizationId}/clients/${clientId}/trainer-sessions`;

export const fetchTrainerPackages = async ({
  assignedByTrainerId,
  clientId,
  organizationId,
}: FetchTrainerPackagesArgs) => {
  const response = await get<BaseApiResponse<TrainerPackages>>(
    `${base(organizationId, clientId)}/packages`,
    { query: assignedByTrainerId ? { assignedByTrainerId } : undefined },
  );
  return response?.data ?? { defaultUserMembershipId: null, packages: [] };
};

/** Marks a finished workout "with trainer" and takes the session (staff). */
export const markTrainerSession = async ({
  assignedByTrainerId,
  clientId,
  feedbackId,
  organizationId,
  userMembershipId,
}: MarkTrainerSessionArgs) => {
  const response = await put<TrainerSessionResult>(
    `${base(organizationId, clientId)}/${feedbackId}`,
    { assignedByTrainerId, userMembershipId },
  );
  return response?.data ?? null;
};

/** Takes the mark off: the session goes back into the package (staff). */
export const releaseTrainerSession = async ({
  clientId,
  feedbackId,
  organizationId,
}: TrainerSessionArgs) => {
  const response = await destroy(
    `${base(organizationId, clientId)}/${feedbackId}`,
  );
  return response ?? null;
};
